import clsx from 'clsx'
import ApplyDialogV2 from '@core/components/ApplyDialogV2'
import { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { useGetPostInfoQuery, useGetPostSettingsQuery } from '@core/app/api/postsApi'
import { useGetAttributesForApplyQuery } from '@core/app/api/profileApi'
import useLocalStorageStore from '@core/utils/hooks/useLocalStorageStore'
import type { IApplyDialogV2LocalStorage } from '@core/app/types'

type ApplyV2ModalContextType = {
    isOpen: boolean
    isExpanded: boolean
    onResizeApply: () => void
    onOpenApply: (component?: ReactNode) => void
    onCloseApply: () => void
    storageData: IApplyDialogV2LocalStorage | null
    content: ReactNode | null
    error: boolean
}

const ApplyV2ModalContext = createContext<ApplyV2ModalContextType>({
    isOpen: false,
    isExpanded: false,
    onOpenApply: () => {
        console.info(`ApplyV2ModalContext openModal() for ${window.location.pathname} is not provided`)
    },
    onCloseApply: () => {
        console.info(`ApplyV2ModalContext closeModal() for ${window.location.pathname} is not provided`)
    },
    onResizeApply: () => {
        console.info(`ApplyV2ModalContext resizeModal() for ${window.location.pathname} is not provided`)
    },
    storageData: null,
    content: null,
    error: false,
})

export const ApplyV2ModalProvider = ({ children }: { children: ReactNode }) => {
    const { id } = useParams()
    const location = useLocation()
    const [isOpen, setIsOpen] = useState(false)
    const [content, setContent] = useState<ReactNode | null>(null)
    const [isExpanded, setIsExpanded] = useState(false)
    const [error, setError] = useState<boolean>(false)
    const [storageData, setStorageData, removeStorageData] = useLocalStorageStore('apply_dialog_v2')
    const [serviceCode] = useLocalStorageStore('service_code')
    const { data: postResponse } = useGetPostInfoQuery(
        {
            id: storageData?.id || id || '',
        },
        { skip: !id || !isOpen }
    )
    const { data: settingsData } = useGetPostSettingsQuery(
        {
            id: storageData?.id || id || '',
            code: serviceCode,
        },
        { skip: !storageData?.id || !isOpen }
    )
    const { data: attributesResponse } = useGetAttributesForApplyQuery({ id: id || '' }, { skip: !id || !isOpen })

    console.log('[ApplyV2ModalContext] attributes:', attributesResponse?.data)

    console.log('[ApplyV2ModalContext] postInfo:', postResponse?.data, settingsData)

    console.log('[ApplyV2ModalContext] id:', id, 'location:', location.pathname)

    useEffect(() => {
        console.log('[ApplyV2ModalContext] postResponse:', postResponse)
        if (postResponse?.data?.ad?.AdID) {
            setStorageData({
                id: postResponse.data.ad.AdID,
                expanded: storageData?.expanded ?? true,
                opened: storageData?.opened ?? false,
            })
        }
    }, [postResponse, storageData])

    useEffect(() => {
        if (storageData) {
            // setIsOpen(true) // TODO
            setIsExpanded(storageData.expanded ?? false)
            setIsOpen(storageData.opened ?? false)
        }
    }, [storageData])

    const onResizeApply = useCallback(() => {
        if (!storageData) {
            console.warn('[ApplyV2ModalContext] Cannot resize modal: storageData is null.')
            return
        }
        setStorageData({ ...storageData, expanded: !storageData.expanded })
    }, [storageData, setStorageData])

    const onOpenApply = (component?: ReactNode) => {
        console.log(storageData, 'onOpenApply')
        setContent(component ?? null)
        setIsOpen(true)
        setStorageData({
            id: storageData?.id || id || '',
            opened: true,
            expanded: true,
        })
    }

    const onCloseApply = useCallback(() => {
        setIsOpen(false)
        setContent(null)
        removeStorageData()
    }, [])

    // useEffect(() => {
    //     if (dayjs(storageData?.job?.expiredDate).diff(dayjs(), 'day') < 0) {
    //         // closeModal()
    //         console.warn(`[ApplyV2ModalContext closeModal()] Modal expired: ${storageData?.job?.expiredDate}`)
    //     }
    // }, [storageData])

    return (
        <ApplyV2ModalContext.Provider
            value={{
                isOpen,
                isExpanded,
                onOpenApply,
                onCloseApply,
                onResizeApply,
                content,
                storageData,
                error,
            }}
        >
            {children}
            {isOpen && <ApplyDialogV2 />}
        </ApplyV2ModalContext.Provider>
    )
}

export const useApplyV2Context = () => useContext(ApplyV2ModalContext)
